import { getProjectBankArchiveJobResult } from '../services/getProjectBankArchiveJobResult.service';

// Cadence tuned so small folders feel instant while a 15-minute build does not
// hammer the API (same start-then-poll shape as the smart camera invoice scan
// and the Jydacom salary transfer).
const FAST_INTERVAL_MS = 1000;
const MEDIUM_INTERVAL_MS = 2000;
const SLOW_INTERVAL_MS = 6000;
const FAST_PHASE_MS = 10000;
const MEDIUM_PHASE_MS = 30000;
// A little over the worker lambda's 900s timeout: if the budget runs out the
// worker is dead (killed without a marker), not merely slow.
const POLL_BUDGET_MS = 16 * 60 * 1000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Polls an archive job until it reaches a terminal state.
 *
 * @param {object} params
 * @param {string} params.projectId
 * @param {string} params.archiveJobId
 * @param {Function} [params.onTick] called with every non-terminal response
 * @param {number} [params.startedAt] job start epoch ms — lets a resumed poll
 *   (page reload) inherit the original budget instead of a fresh 16 minutes
 * @returns {Promise<object>} the DONE or FAILED response, or {status:'TIMEOUT'}
 */
export const pollProjectBankArchiveJob = async ({
  projectId,
  archiveJobId,
  onTick,
  startedAt,
}) => {
  const budgetStart = startedAt || Date.now();
  const deadline = budgetStart + POLL_BUDGET_MS;
  let firstPoll = true;

  while (true) {
    if (!firstPoll) {
      const elapsed = Date.now() - budgetStart;
      const interval =
        elapsed < FAST_PHASE_MS
          ? FAST_INTERVAL_MS
          : elapsed < MEDIUM_PHASE_MS
            ? MEDIUM_INTERVAL_MS
            : SLOW_INTERVAL_MS;
      await wait(interval);
    }
    firstPoll = false;

    let result;
    try {
      result = await getProjectBankArchiveJobResult(projectId, archiveJobId);
    } catch (error) {
      // A transient network/API error is not a job failure — keep polling
      // until the budget runs out.
      console.error('Archive job poll failed:', error);
      result = null;
    }

    if (result?.status === 'DONE' || result?.status === 'FAILED') {
      return result;
    }

    if (result && onTick) {
      onTick(result);
    }

    if (Date.now() > deadline) {
      return { status: 'TIMEOUT' };
    }
  }
};
